/**
 * ホール管理共通ライブラリ
 * ホール検索、ホール選択ダイアログ
 * 
 * 設計ドキュメント: 06_19_ホール検索処理詳細設計.md
 */

const { info, error: logError, debug } = require('./logging');
const { handleApiError } = require('./error-handling');

/**
 * API Gateway URL
 * ホール検索APIのエンドポイントURL
 */
// ビルド時に GitHub Secrets から注入される想定。
// ビルド時に process.env.API_GATEWAY_URL が文字列リテラルに置き換えられる
const API_GATEWAY_URL = process.env.API_GATEWAY_URL || '';
if (!API_GATEWAY_URL) {
  throw new Error('API_GATEWAY_URL is not defined. Set it via build-time environment (e.g., GitHub Secrets).');
}

/**
 * セッション情報を取得
 * @returns {Object|null} セッション情報、またはnull
 */
function getSessionInfo() {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
    return null;
  }
  
  const storageKey = 'unifiedAuthHallInfo';
  const sessionInfoStr = localStorage.getItem(storageKey);
  if (!sessionInfoStr) {
    return null;
  }
  
  try {
    return JSON.parse(sessionInfoStr);
  } catch (e) {
    console.error('Failed to parse session info:', e);
    return null;
  }
}

/**
 * 選択ホール情報のみを更新
 * @param {Object} selectedHall - 選択されたホール情報
 * @throws {Error} 保存に失敗した場合
 */
function updateSelectedHall(selectedHall) {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
    throw new Error('セッション情報の更新に失敗しました');
  }
  
  const sessionInfo = getSessionInfo();
  
  // セッション情報が存在しない場合は新規作成
  if (!sessionInfo) {
    const newSessionInfo = {
      selectedHall: selectedHall,
      lastUpdated: new Date().toISOString()
    };
    
    const storageKey = 'unifiedAuthHallInfo';
    try {
      localStorage.setItem(storageKey, JSON.stringify(newSessionInfo));
    } catch (e) {
      throw new Error('セッション情報の更新に失敗しました');
    }
    return;
  }
  
  // セッション情報が存在する場合は更新
  sessionInfo.selectedHall = selectedHall;
  sessionInfo.lastUpdated = new Date().toISOString();
  
  const storageKey = 'unifiedAuthHallInfo';
  try {
    localStorage.setItem(storageKey, JSON.stringify(sessionInfo));
  } catch (e) {
    throw new Error('セッション情報の更新に失敗しました');
  }
}

/**
 * ホール検索APIを呼び出し、全件取得してレスポンス件数に応じて処理を分岐する
 * 
 * 設計ドキュメント: 06_19_ホール検索処理詳細設計.md
 * 
 * @param {number} [pref] - 都道府県ID（任意、1以上47以下）
 * @returns {Promise<void>} 処理完了時に解決されるPromise（データは返却せず、ローカルストレージに保存）
 * @throws {Error} バリデーションエラーまたはAPI呼び出しエラー
 * 
 * @typedef {Object} Hall
 * @property {string} hallId - ホールID
 * @property {string} hallName - ホール名
 * @property {string} address - 住所
 * @property {string|null} [phone] - 電話番号（任意）
 * @property {number} prefId - 都道府県ID
 * 
 * @typedef {Object} HallSearchResponse
 * @property {Hall[]} halls - ホール情報の配列
 * @property {number} total - 総件数
 * @property {Prefecture[]} prefectures - ホールが存在する都道府県の配列
 * 
 * @typedef {Object} Prefecture
 * @property {number} prefId - 都道府県ID
 */
async function searchHalls(pref) {
  const startTime = Date.now();
  
  // ブラウザ環境チェック
  if (typeof window === 'undefined') {
    const error = new Error('E005-0003: ブラウザ環境で実行する必要があります');
    error.code = 'E005-0003';
    error.statusCode = 500;
    
    logError('ホール検索処理で環境エラーが発生しました', {
      event: 'error',
      category: 'system',
      http: {
        method: 'GET',
        path: '/api/halls/search',
        status: 500
      },
      data: {
        message_id: 'E005-0003'
      }
    });
    
    throw error;
  }

  // API Gateway URLの取得
  const apiGatewayUrl = API_GATEWAY_URL;
  if (!apiGatewayUrl || typeof apiGatewayUrl !== 'string' || apiGatewayUrl.trim().length === 0) {
    const error = new Error('E005-0003: 環境変数が設定されていない');
    error.code = 'E005-0003';
    error.statusCode = 500;
    
    logError('ホール検索処理で環境変数エラーが発生しました', {
      event: 'error',
      category: 'system',
      http: {
        method: 'GET',
        path: '/api/halls/search',
        status: 500
      },
      data: {
        message_id: 'E005-0003'
      }
    });
    
    throw error;
  }

  // 1. パラメータのバリデーション
  let prefNum = null;
  if (pref !== undefined && pref !== null && pref !== '') {
    prefNum = typeof pref === 'number' ? pref : parseInt(pref, 10);
    
    // バリデーション: 整数型チェック、範囲チェック（1以上47以下）
    if (isNaN(prefNum) || !Number.isInteger(prefNum) || prefNum < 1 || prefNum > 47) {
      const error = new Error('E004-0001: パラメータが不正です（pref）');
      error.code = 'E004-0001';
      error.statusCode = 400;
      
      logError('ホール検索処理でバリデーションエラーが発生しました', {
        event: 'error',
        category: 'validation',
        http: {
          method: 'GET',
          path: '/api/halls/search',
          status: 400
        },
        data: {
          message_id: 'E004-0001',
          pref: pref
        }
      });
      
      throw error;
    }
  }

  // アクセスログ出力
  info('ホール検索リクエストを受信しました', {
    event: 'hall_search_request',
    category: 'hall',
    http: {
      method: 'GET',
      path: '/api/halls/search'
    },
    data: {
      pref: prefNum !== null ? prefNum : undefined
    }
  });

  // 2. ホール検索APIを呼び出して全件取得（リトライ処理付き）
  let apiUrl = `${apiGatewayUrl}/api/halls/search`;
  if (prefNum !== null) {
    apiUrl += `?pref=${prefNum}`;
  }
  
  const maxRetries = 3;
  let lastError = null;
  let responseData = null;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    let response;
    try {
      response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include' // Cookieを送信するために必要（access_tokenを含むHttpOnly Cookie）
      });
    } catch (fetchError) {
      // ネットワークエラーの場合、リトライ可能
      lastError = fetchError;
      
      if (attempt < maxRetries) {
        // リトライ前に待機（指数バックオフ）
        await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt - 1) * 1000));
        continue;
      }
      
      // 最大リトライ回数に達した場合
      const networkError = new Error('E005-0001: サーバーエラーが発生しました');
      networkError.code = 'E005-0001';
      networkError.statusCode = 500;
      networkError.originalError = fetchError;
      
      logError('ホール検索処理でネットワークエラーが発生しました', {
        event: 'error',
        category: 'network',
        http: {
          method: 'GET',
          path: '/api/halls/search',
          status: 500
        },
        data: {
          message_id: 'E005-0001',
          pref: prefNum !== null ? prefNum : undefined,
          retry_attempt: attempt
        }
      });
      
      throw networkError;
    }
    
    // レスポンスの解析
    try {
      responseData = await response.json();
    } catch (parseError) {
      // レスポンス解析エラーはリトライ対象外
      const error = new Error('E005-0001: サーバーエラーが発生しました');
      error.code = 'E005-0001';
      error.statusCode = 500;
      error.originalError = parseError;
      
      logError('ホール検索処理でレスポンス解析エラーが発生しました', {
        event: 'error',
        category: 'api',
        http: {
          method: 'GET',
          path: '/api/halls/search',
          status: 500
        },
        data: {
          message_id: 'E005-0001',
          pref: prefNum !== null ? prefNum : undefined
        }
      });
      
      throw error;
    }
    
    // エラーレスポンスの処理
    if (!response.ok) {
      // 認証エラーの場合（401）
      if (response.status === 401) {
        const apiError = handleApiError(response, responseData, 'ユーザー ID またはパスワードが正しくありません');
        
        logError('ホール検索処理で認証エラーが発生しました', {
          event: 'error',
          category: 'auth',
          http: {
            method: 'GET',
            path: '/api/halls/search',
            status: 401
          },
          data: {
            message_id: apiError.code || 'E001-0001',
            pref: prefNum !== null ? prefNum : undefined
          }
        });
        
        throw apiError;
      }
      
      // 5xxエラー（サーバーエラー）はリトライ可能（429レート制限エラーを除く）
      const isRetryableError = response.status >= 500 && response.status !== 429;
      
      if (isRetryableError && attempt < maxRetries) {
        // リトライ前に待機（指数バックオフ）
        await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt - 1) * 1000));
        continue;
      }
      
      const apiError = handleApiError(response, responseData, 'サーバーエラーが発生しました');
      
      logError('ホール検索処理でAPIエラーが発生しました', {
        event: 'error',
        category: 'api',
        http: {
          method: 'GET',
          path: '/api/halls/search',
          status: apiError.statusCode
        },
        data: {
          message_id: apiError.code || 'E005-0001',
          pref: prefNum !== null ? prefNum : undefined,
          retry_attempt: attempt
        }
      });
      
      throw apiError;
    }
    
    // 成功した場合はループを抜ける
    break;
  }
  
  // リトライが全て失敗した場合
  if (!responseData) {
    throw lastError || new Error('E005-0001: サーバーエラーが発生しました');
  }
  
  // 3. レスポンスからdataを取得
  if (!responseData.data) {
    const error = new Error('E005-0001: サーバーエラーが発生しました');
    error.code = 'E005-0001';
    error.statusCode = 500;
    
    logError('ホール検索処理でレスポンスデータが不正です', {
      event: 'error',
      category: 'api',
      http: {
        method: 'GET',
        path: '/api/halls/search',
        status: 500
      },
      data: {
        message_id: 'E005-0001',
        pref: prefNum !== null ? prefNum : undefined
      }
    });
    
    throw error;
  }
  
  const halls = responseData.data.halls || [];
  const total = responseData.data.total || 0;
  const prefectures = responseData.data.prefectures || [];
  
  // アクセスログ出力
  const endTime = Date.now();
  const duration = endTime - startTime;
  info('ホール検索処理が正常に完了しました', {
    event: 'hall_search_completed',
    category: 'hall',
    perf: {
      duration_ms: duration
    },
    data: {
      pref: prefNum !== null ? prefNum : undefined,
      total: total,
      prefectureCount: prefectures.length
    }
  });
  
  // 4. レスポンス件数に応じて分岐
  if (halls.length === 0) {
    // レスポンスが0件の場合
    // 06_19設計書に基づき、06_20_ホール選択ダイアログを呼び出し（空のホール一覧データを渡す）
    // ダイアログ上部のエラー表示領域にエラーメッセージを表示（メッセージID：E000-0011）
    // 注意: エラーメッセージを表示するために、ダイアログを表示する必要がある
    await showHallSelectionDialog([]);
    
    // エラーログを出力
    logError('ホールが見つかりません', {
      event: 'error',
      category: 'hall',
      data: {
        message_id: 'E000-0011',
        pref: prefNum !== null ? prefNum : undefined,
        total: total
      }
    });
    
    // 注意: エラーメッセージはダイアログ内で表示される
    // ここではエラーをスローしない（APIは正常に返却されているため）
    return; // 処理完了（データは返却しない）
  } else if (halls.length === 1) {
    // レスポンスが1件の場合
    // 取得したホール情報をローカルストレージに保存（updateSelectedHall()を使用）
    try {
      updateSelectedHall(halls[0]);
      
      debug('ホール情報をローカルストレージに保存しました', {
        event: 'hall_saved',
        category: 'hall',
        data: {
          hallId: halls[0].hallId,
          hallName: halls[0].hallName
        }
      });
    } catch (saveError) {
      // 保存に失敗した場合（ストレージ容量不足、プライベートモード等）
      const error = new Error('E005-0001: サーバーエラーが発生しました');
      error.code = 'E005-0001';
      error.statusCode = 500;
      error.originalError = saveError;
      
      logError('ホール情報の保存に失敗しました', {
        event: 'error',
        category: 'storage',
        http: {
          method: 'GET',
          path: '/api/halls/search',
          status: 500
        },
        data: {
          message_id: 'E005-0001',
          pref: prefNum !== null ? prefNum : undefined,
          error: saveError.message
        }
      });
      
      throw error;
    }
    
    // 処理完了（データは返却しない）
    return;
  } else {
    // レスポンスが2件以上の場合
    // 06_20_ホール選択ダイアログを呼び出し（取得したホール一覧データを渡す）
    const selectedHall = await showHallSelectionDialog(halls);
    
    if (selectedHall === null) {
      // ユーザーがキャンセルした場合（nullが返却された場合）
      const error = new Error('E004-0001: パラメータが不正です');
      error.code = 'E004-0001';
      error.statusCode = 400;
      
      logError('ホール選択がキャンセルされました', {
        event: 'error',
        category: 'validation',
        http: {
          method: 'GET',
          path: '/api/halls/search',
          status: 400
        },
        data: {
          message_id: 'E004-0001',
          pref: prefNum !== null ? prefNum : undefined
        }
      });
      
      throw error;
    }
    
    // ユーザーがホールを選択した場合: 選択されたホール情報をローカルストレージに保存
    try {
      updateSelectedHall(selectedHall);
      
      debug('選択されたホール情報をローカルストレージに保存しました', {
        event: 'hall_saved',
        category: 'hall',
        data: {
          hallId: selectedHall.hallId,
          hallName: selectedHall.hallName
        }
      });
    } catch (saveError) {
      // 保存に失敗した場合（ストレージ容量不足、プライベートモード等）
      const error = new Error('E005-0001: サーバーエラーが発生しました');
      error.code = 'E005-0001';
      error.statusCode = 500;
      error.originalError = saveError;
      
      logError('選択されたホール情報の保存に失敗しました', {
        event: 'error',
        category: 'storage',
        http: {
          method: 'GET',
          path: '/api/halls/search',
          status: 500
        },
        data: {
          message_id: 'E005-0001',
          pref: prefNum !== null ? prefNum : undefined,
          error: saveError.message
        }
      });
      
      throw error;
    }
    
    // 処理完了（データは返却しない）
    return;
  }
}

/**
 * 都道府県IDから都道府県名を取得
 * @param {number} prefId - 都道府県ID（1-47）
 * @returns {string} 都道府県名
 */
function getPrefectureName(prefId) {
  const prefectures = [
    '北海道', '青森県', '岩手県', '宮城県', '秋田県', '山形県', '福島県',
    '茨城県', '栃木県', '群馬県', '埼玉県', '千葉県', '東京都', '神奈川県',
    '新潟県', '富山県', '石川県', '福井県', '山梨県', '長野県', '岐阜県',
    '静岡県', '愛知県', '三重県', '滋賀県', '京都府', '大阪府', '兵庫県',
    '奈良県', '和歌山県', '鳥取県', '島根県', '岡山県', '広島県', '山口県',
    '徳島県', '香川県', '愛媛県', '高知県', '福岡県', '佐賀県', '長崎県',
    '熊本県', '大分県', '宮崎県', '鹿児島県', '沖縄県'
  ];
  return prefectures[prefId - 1] || `都道府県${prefId}`;
}

/**
 * ホール選択ダイアログを表示
 * 
 * 設計ドキュメント: 06_20_ホール選択ダイアログ処理詳細設計.md
 * 
 * @param {Hall[]} halls - ホール情報の配列（2件以上、または0件）
 * @returns {Promise<Hall|null>} 選択されたホール情報、またはnull（キャンセル時）
 * @throws {Error} バリデーションエラー
 */
async function showHallSelectionDialog(halls) {
  const startTime = Date.now();
  
  // ブラウザ環境チェック
  if (typeof window === 'undefined') {
    const error = new Error('E005-0003: ブラウザ環境で実行する必要があります');
    error.code = 'E005-0003';
    error.statusCode = 500;
    
    logError('ホール選択ダイアログ処理で環境エラーが発生しました', {
      event: 'error',
      category: 'system',
      data: {
        message_id: 'E005-0003'
      }
    });
    
    throw error;
  }

  // パラメータのバリデーション
  if (!Array.isArray(halls)) {
    const error = new Error('E004-0001: パラメータが不正です（halls）');
    error.code = 'E004-0001';
    error.statusCode = 400;
    
    logError('ホール選択ダイアログ処理でバリデーションエラーが発生しました', {
      event: 'error',
      category: 'validation',
      data: {
        message_id: 'E004-0001',
        hall_count: halls ? (Array.isArray(halls) ? halls.length : 'N/A') : 0
      }
    });
    
    throw error;
  }

  // バリデーション: 2件以上である必要がある（ただし、0件の場合は許可：エラーメッセージ表示用）
  // 1件の場合はエラー（2件以上である必要がある）
  // 0件の場合は許可（エラーメッセージ表示用、06_19から呼び出される場合がある）
  if (halls.length === 1) {
    const error = new Error('E004-0001: パラメータが不正です（halls）');
    error.code = 'E004-0001';
    error.statusCode = 400;
    
    logError('ホール選択ダイアログ処理でバリデーションエラーが発生しました', {
      event: 'error',
      category: 'validation',
      data: {
        message_id: 'E004-0001',
        hall_count: halls.length
      }
    });
    
    throw error;
  }

  // アクセスログ出力
  info('ホール選択ダイアログを表示します', {
    event: 'hall_selection_dialog_open',
    category: 'hall',
    data: {
      hall_count: halls.length
    }
  });

  // ダイアログの作成と表示
  return new Promise((resolve, reject) => {
    // 既存のダイアログがあれば削除
    const existingDialog = document.getElementById('hall-selection-dialog');
    if (existingDialog) {
      existingDialog.remove();
    }

    // ダイアログのオーバーレイとコンテナを作成
    const overlay = document.createElement('div');
    overlay.id = 'hall-selection-dialog-overlay';
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(0, 0, 0, 0.5);
      z-index: 10000;
      display: flex;
      justify-content: center;
      align-items: center;
    `;

    const dialog = document.createElement('div');
    dialog.id = 'hall-selection-dialog';
    dialog.style.cssText = `
      background-color: white;
      border-radius: 8px;
      padding: 24px;
      max-width: 800px;
      width: 90%;
      max-height: 90vh;
      overflow-y: auto;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
      position: relative;
    `;

    // 現在のホール一覧（状態管理）
    let currentHalls = [...halls];
    let currentPrefectures = [];

    // 都道府県IDを抽出して都道府県リストを生成
    const extractPrefectures = (hallList) => {
      const prefIds = [...new Set(
        hallList
          .map(hall => hall.prefId)
          .filter(prefId => prefId != null && prefId >= 1 && prefId <= 47)
      )].sort((a, b) => a - b);
      return prefIds.map(prefId => ({ prefId }));
    };

    // 初期都道府県リストを生成
    currentPrefectures = extractPrefectures(halls);

    // エラーメッセージ表示関数
    const showError = (messageId, message) => {
      const errorArea = dialog.querySelector('#hall-selection-error');
      if (errorArea) {
        errorArea.textContent = message || 'エラーが発生しました';
        errorArea.style.display = 'block';
        errorArea.style.color = '#d32f2f';
        errorArea.style.backgroundColor = '#ffebee';
        errorArea.style.padding = '12px';
        errorArea.style.borderRadius = '4px';
        errorArea.style.marginBottom = '16px';
      }
    };

    const hideError = () => {
      const errorArea = dialog.querySelector('#hall-selection-error');
      if (errorArea) {
        errorArea.style.display = 'none';
      }
    };

    // ホール一覧を更新する関数
    const updateHallList = (hallList) => {
      const tbody = dialog.querySelector('#hall-selection-tbody');
      if (!tbody) return;

      tbody.innerHTML = '';

      if (hallList.length === 0) {
        const row = document.createElement('tr');
        row.innerHTML = '<td colspan="5" style="text-align: center; padding: 20px;">ホールが見つかりません</td>';
        tbody.appendChild(row);
        return;
      }

      hallList.forEach(hall => {
        const row = document.createElement('tr');
        
        // XSS対策: textContentを使用して安全にデータを挿入
        const selectCell = document.createElement('td');
        selectCell.style.cssText = 'padding: 8px; text-align: center;';
        const selectBtn = document.createElement('button');
        selectBtn.className = 'hall-select-btn';
        selectBtn.setAttribute('data-hall-id', String(hall.hallId || ''));
        selectBtn.style.cssText = 'padding: 6px 16px; background-color: #1976d2; color: white; border: none; border-radius: 4px; cursor: pointer;';
        selectBtn.textContent = '選択';
        selectCell.appendChild(selectBtn);
        
        const codeCell = document.createElement('td');
        codeCell.style.cssText = 'padding: 8px;';
        codeCell.textContent = hall.hallId || '';
        
        const nameCell = document.createElement('td');
        nameCell.style.cssText = 'padding: 8px;';
        nameCell.textContent = hall.hallName || '';
        
        const addressCell = document.createElement('td');
        addressCell.style.cssText = 'padding: 8px;';
        addressCell.textContent = hall.address || '';
        
        const phoneCell = document.createElement('td');
        phoneCell.style.cssText = 'padding: 8px;';
        phoneCell.textContent = hall.phone || '';
        
        row.appendChild(selectCell);
        row.appendChild(codeCell);
        row.appendChild(nameCell);
        row.appendChild(addressCell);
        row.appendChild(phoneCell);
        
        tbody.appendChild(row);
      });

      // 結果数表示を更新
      const resultCount = dialog.querySelector('#hall-selection-result-count');
      if (resultCount) {
        resultCount.textContent = `${hallList.length}件表示します。`;
      }
    };

    // 都道府県プルダウンを更新する関数
    const updatePrefectureDropdown = (prefectureList) => {
      const select = dialog.querySelector('#hall-selection-pref');
      if (!select) return;

      // 現在の選択値を保持
      const currentValue = select.value;

      // オプションをクリア（プレースホルダーを除く）
      while (select.children.length > 1) {
        select.removeChild(select.lastChild);
      }

      // 都道府県オプションを追加
      prefectureList.forEach(pref => {
        const option = document.createElement('option');
        option.value = pref.prefId;
        option.textContent = getPrefectureName(pref.prefId);
        select.appendChild(option);
      });

      // 以前の選択値を復元（存在する場合）
      if (currentValue && Array.from(select.options).some(opt => opt.value === currentValue)) {
        select.value = currentValue;
      } else {
        select.value = '';
      }
    };

    // ホール検索APIを呼び出す関数
    const searchHallsByPrefecture = async (prefId) => {
      hideError();
      
      // 検索中の都道府県IDを保持（ログ用）
      const filterPrefId = prefId;

      const apiGatewayUrl = API_GATEWAY_URL;
      if (!apiGatewayUrl || typeof apiGatewayUrl !== 'string' || apiGatewayUrl.trim().length === 0) {
        showError('E005-0003', '環境変数が設定されていません');
        return;
      }

      let apiUrl = `${apiGatewayUrl}/api/halls/search`;
      if (prefId !== null && prefId !== '') {
        apiUrl += `?pref=${prefId}`;
      }

      const maxRetries = 3;
      let lastError = null;
      let responseData = null;

      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        let response;
        try {
          response = await fetch(apiUrl, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json'
            },
            credentials: 'include'
          });
        } catch (fetchError) {
          lastError = fetchError;
          
          if (attempt < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt - 1) * 1000));
            continue;
          }
          
          showError('E005-0001', 'サーバーエラーが発生しました');
          logError('ホール検索処理でネットワークエラーが発生しました', {
            event: 'error',
            category: 'network',
            http: {
              method: 'GET',
              path: '/api/halls/search',
              status: 500
            },
            data: {
              message_id: 'E005-0001',
              filter_pref: filterPrefId !== null && filterPrefId !== '' ? filterPrefId : undefined,
              retry_attempt: attempt
            }
          });
          return;
        }
        
        try {
          responseData = await response.json();
        } catch (parseError) {
          showError('E005-0001', 'サーバーエラーが発生しました');
          logError('ホール検索処理でレスポンス解析エラーが発生しました', {
            event: 'error',
            category: 'api',
            http: {
              method: 'GET',
              path: '/api/halls/search',
              status: 500
            },
            data: {
              message_id: 'E005-0001',
              filter_pref: filterPrefId !== null && filterPrefId !== '' ? filterPrefId : undefined
            }
          });
          return;
        }
        
        if (!response.ok) {
          if (response.status === 401) {
            const apiError = handleApiError(response, responseData, 'ユーザー ID またはパスワードが正しくありません');
            showError('E001-0001', apiError.message);
            logError('ホール検索処理で認証エラーが発生しました', {
              event: 'error',
              category: 'auth',
              http: {
                method: 'GET',
                path: '/api/halls/search',
                status: 401
              },
              data: {
                message_id: 'E001-0001',
                filter_pref: filterPrefId !== null && filterPrefId !== '' ? filterPrefId : undefined
              }
            });
            return;
          }
          
          const isRetryableError = response.status >= 500 && response.status !== 429;
          
          if (isRetryableError && attempt < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt - 1) * 1000));
            continue;
          }
          
          const apiError = handleApiError(response, responseData, 'サーバーエラーが発生しました');
          showError(apiError.code || 'E005-0001', apiError.message);
          logError('ホール検索処理でAPIエラーが発生しました', {
            event: 'error',
            category: 'api',
            http: {
              method: 'GET',
              path: '/api/halls/search',
              status: apiError.statusCode
            },
            data: {
              message_id: apiError.code || 'E005-0001',
              filter_pref: filterPrefId !== null && filterPrefId !== '' ? filterPrefId : undefined,
              retry_attempt: attempt
            }
          });
          return;
        }
        
        break;
      }
      
      if (!responseData || !responseData.data) {
        showError('E005-0001', 'サーバーエラーが発生しました');
        return;
      }
      
      const searchHalls = responseData.data.halls || [];
      const searchTotal = responseData.data.total || 0;
      const searchPrefectures = responseData.data.prefectures || [];

      // 検索結果の件数に応じて分岐
      if (searchHalls.length === 0) {
        showError('E000-0011', 'ホールが見つかりません');
        currentHalls = [];
        updateHallList([]);
      } else {
        // 1件以上の場合、ホール一覧を表示（1件の場合も選択可能にする）
        currentHalls = searchHalls;
        // 都道府県ドロップダウンは初期の全都道府県リストを保持（検索結果の都道府県リストは使わない）
        updateHallList(searchHalls);
        updatePrefectureDropdown(currentPrefectures);
      }
    };

    // ダイアログのHTMLを構築
    dialog.innerHTML = `
      <div style="margin-bottom: 16px;">
        <h2 style="margin: 0 0 16px 0; font-size: 20px; font-weight: bold;">店舗選択</h2>
        
        <div id="hall-selection-error" style="display: none;"></div>
        
        <div style="margin-bottom: 16px;">
          <label for="hall-selection-pref" style="display: block; margin-bottom: 8px; font-weight: bold;">都道府県絞り込み</label>
          <select id="hall-selection-pref" style="
            width: 100%;
            padding: 8px;
            border: 1px solid #ccc;
            border-radius: 4px;
            font-size: 14px;
          ">
            <option value="">選択してください</option>
          </select>
        </div>
        
        <div style="margin-bottom: 16px;">
          <button id="hall-selection-search-btn" style="
            padding: 8px 16px;
            background-color: #1976d2;
            color: white;
            border: none;
            border-radius: 4px;
            cursor: pointer;
            font-size: 14px;
          ">絞り込んで検索</button>
        </div>
        
        <div id="hall-selection-result-count" style="margin-bottom: 8px; font-size: 14px;">
          ${halls.length}件表示します。
        </div>
        
        <div style="overflow-x: auto; margin-bottom: 16px;">
          <table style="width: 100%; border-collapse: collapse; border: 1px solid #ddd;">
            <thead>
              <tr style="background-color: #f5f5f5;">
                <th style="padding: 8px; border: 1px solid #ddd; text-align: center;">選択</th>
                <th style="padding: 8px; border: 1px solid #ddd; text-align: left;">コード</th>
                <th style="padding: 8px; border: 1px solid #ddd; text-align: left;">名称</th>
                <th style="padding: 8px; border: 1px solid #ddd; text-align: left;">住所</th>
                <th style="padding: 8px; border: 1px solid #ddd; text-align: left;">電話番号</th>
              </tr>
            </thead>
            <tbody id="hall-selection-tbody">
            </tbody>
          </table>
        </div>
        
        <div style="text-align: right; margin-top: 16px;">
          <button id="hall-selection-close-btn" style="
            padding: 8px 16px;
            background-color: #757575;
            color: white;
            border: none;
            border-radius: 4px;
            cursor: pointer;
            font-size: 14px;
          ">閉じる</button>
        </div>
        
        <div style="margin-top: 24px; text-align: center; font-size: 12px; color: #666;">
          Copyright © Sammy Networks Co., Ltd. All Rights Reserved.
        </div>
      </div>
    `;

    // 初期都道府県プルダウンを設定
    updatePrefectureDropdown(currentPrefectures);
    
    // 初期ホール一覧を表示
    updateHallList(halls);
    
    // 0件の場合はエラーメッセージを表示
    if (halls.length === 0) {
      showError('E000-0011', 'ホールが見つかりません');
    }

    // イベントリスナーの設定
    const searchBtn = dialog.querySelector('#hall-selection-search-btn');
    const closeBtn = dialog.querySelector('#hall-selection-close-btn');
    const prefSelect = dialog.querySelector('#hall-selection-pref');

    // 絞り込んで検索ボタン
    const handleSearchClick = async () => {
      const prefValue = prefSelect.value;
      const prefId = prefValue === '' ? null : parseInt(prefValue, 10);
      await searchHallsByPrefecture(prefId);
    };
    searchBtn.addEventListener('click', handleSearchClick);

    // 閉じるボタン
    // 設計書に基づき、閉じるボタンクリック時はエラーメッセージを表示して画面を非表示にしない
    const handleCloseClick = () => {
      showError('E004-0001', 'ホールを選択してください');
    };
    closeBtn.addEventListener('click', handleCloseClick);

    // オーバーレイクリックで閉じる
    // 設計書に基づき、画面範囲外クリックでは画面を非表示にしない
    // イベントリスナーは削除（何もしない）
    
    // クリーンアップ関数（メモリリーク対策）
    const cleanup = () => {
      searchBtn.removeEventListener('click', handleSearchClick);
      closeBtn.removeEventListener('click', handleCloseClick);
      overlay.remove();
    };

    // 選択ボタンのイベント（動的に追加される要素なので、イベント委譲を使用）
    dialog.addEventListener('click', (e) => {
      if (e.target.classList.contains('hall-select-btn')) {
        const hallId = e.target.getAttribute('data-hall-id');
        const selectedHall = currentHalls.find(hall => hall.hallId === hallId);
        
        if (selectedHall) {
          const endTime = Date.now();
          const duration = endTime - startTime;
          
          info('ホールが選択されました', {
            event: 'hall_selected',
            category: 'hall',
            perf: {
              duration_ms: duration
            },
            data: {
              selected_hall_id: selectedHall.hallId,
              hall_count: currentHalls.length
            }
          });
          
          // クリーンアップしてから解決
          cleanup();
          resolve(selectedHall);
        }
      }
    });

    // ダイアログをDOMに追加
    overlay.appendChild(dialog);
    document.body.appendChild(overlay);
  });
}

/**
 * ホール情報取得
 * @param {string} hallId - ホールID
 * @returns {Promise<Hall>} ホール情報
 */
async function getHallInfo(hallId) {
  // TODO: 実装が必要
  throw new Error('Not implemented');
}

module.exports = {
  // ホール検索
  searchHalls,
  
  // ホール選択ダイアログ
  showHallSelectionDialog,
  
  // ホール情報取得
  getHallInfo,
};

